import React from 'react';
import axios from 'axios';

import { useModal } from '../../Modal/ModalContext';
import { useTimeModal } from '../../TimeModal/TimeModalContext';
import { formatNull, formatDateTime } from '../../../Utils/GlobalUtils';

import Loading from '../../Loading';
import LoadingLines from '../../LoadingLines';
import WarningPanel from '../../WarningPanel';

import SearchAM from './Components/SearchAM';

import styles from '../../../styles/DynamicModal.module.css';

import IconContainer from '../../IconContainer';
import { ReactComponent as XIcon } from '../../../icons/x-close.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';

const AttachAM = ({ caseId, caseNumber, step, openListMailing, closeModal, onSuccess }) => {
    const { openModal } = useModal();
    const { openTimeModal } = useTimeModal();

    const [regions, setRegions] = React.useState([]);
    const [emails, setEmails] = React.useState([]);

    const [loading, setLoading] = React.useState(true);
    const [warning, setWarning] = React.useState('');
    const [status, setStatus] = React.useState('');
    
    const [action, setAction] = React.useState(null);

    React.useEffect(() => {
        if (step) setAction(step);
    }, []);

    React.useEffect(() => {
        axios.get('/api/get-all-regions')
        .then((response) => {
            setRegions(response.data);
        })
        .catch((error) => {
            console.error('Ошибка загрузки email-ов:', error);
            setWarning('Не удалось загрузить список регионов');
        })

        axios.get('/api/get-am-emails')
        .then((response) => {
            setEmails(response.data);
        })
        .catch((error) => {
            console.error('Ошибка загрузки email-ов:', error);
            setWarning('Не удалось загрузить электронные адреса арбитражных управляющих');
        })
        .finally(() => {
            setLoading(false);
        })
    }, []);

    const [receivedLettersEmails, setReceivedLettersEmails] = React.useState([]);
    const [isApplicationDistributionWas, setIsApplicationDistributionWas] = React.useState(false);

    React.useEffect(() => {
        axios.get(`/api/get-ams-who-received-letters-for-application?case_id=${caseId}`)
        .then((response) => {
            if (response.data.length!==0) setIsApplicationDistributionWas(true);
            setReceivedLettersEmails(response.data);
        })
        .catch((error) => {
            console.error('Ошибка загрузки email-ов:', error);
            openTimeModal('Не удалось загрузить список емейлов управляющих, которым уже приходило предложение', 'error');
        })
    }, []);

    const [expandedRegions, setExpandedRegions] = React.useState([]);
    const [selectedEmails, setSelectedEmails] = React.useState([]);

    const toggleRegionExpand = (id) => {
        setExpandedRegions((prev) =>
            prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
        );
    };

    const toggleEmailSelect = (email) => {
        if (receivedLettersEmails.some(item => item.email === email)) return openTimeModal('Данному управляющему уже была отправлена заявка', 'warning');
        setSelectedEmails((prev) =>
            prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
        );
    };

    const [isAllEmailsSelected, setIsAllEmailsSelected] = React.useState(false);

    const handleToggleSelectAll = () => {
        if (isAllEmailsSelected) {
            setSelectedEmails([]);
            setSelectedRegions([]);
        } else {
            const allEmailStrings = [...new Set(
                emails
                    .map(item => item.email)
                    .filter(email => !receivedLettersEmails.some(r => r.email === email))
            )];
            setSelectedEmails(allEmailStrings);

            const regionsWithEmails = regions
                .filter((region) => emails.some((email) =>
                    email.region_id === region.id &&
                    !receivedLettersEmails.some(r => r.email === email.email)
                ))
                .map((r) => r.id);
            setSelectedRegions(regionsWithEmails);
        }
        setIsAllEmailsSelected(!isAllEmailsSelected);
    };


    // Нажатие кнопки Отправить рассылку
    const handleSendMailing = () => {
        const token = localStorage.getItem('token');
        
        setLoading(true);
        setStatus(<a><Loading type='inline' /></a>);

        axios.post('/api/send-offers-case', {
            emails: selectedEmails,
            case_id: caseId
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        .then(() => {
            openTimeModal(`Рассылка успешно проведена`, 'success');
            closeModal();
            onSuccess();
        })
        .catch((error) => {
            console.error('Ошибка при отправке рассылки:', error);
            setStatus(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const attachAMForCase = (userId) => {
        const token = localStorage.getItem('token');

        axios.put('/api/attach-am-for-case', {
            case_id: caseId,
            user_id: userId
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        })
       .then(() => {
            openTimeModal('Арбитражный управляющий успешно выбран в обход рассылки', 'success');
            closeModal();
            onSuccess();
       })
       .catch((error) => {
            console.error(error);
            setStatus(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
        })
    };

    const handleSave = (userId) => {
        setLoading(true);
        setStatus(<a><Loading type='inline' /></a>);

        if (action=='withMailing') handleSendMailing();
        if (action=='withoutMailing') attachAMForCase(userId);

        setLoading(false);
    };

    const [selectedRegions, setSelectedRegions] = React.useState([]);

    const [searchTerm, setSearchTerm] = React.useState('');
    const mainRef = React.useRef(null);
    const regionRefs = React.useRef({});

    const handleSearchRegion = (value) => {
        setSearchTerm(value);

        if (!value.trim()) return;

        const found = regions.find(region => 
            region.name.toLowerCase().includes(value.toLowerCase())
        );

        if (found && regionRefs.current[found.id] && mainRef.current) {
            const container = mainRef.current;
            const target = regionRefs.current[found.id];

            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();

            const scrollOffset = 
                targetRect.top - containerRect.top 
                - (container.clientHeight / 2) 
                + (targetRect.height / 2);

            container.scrollTop += scrollOffset;
        }
    };

    const funcBackButton = () => {
        if (openListMailing) {
            return openListMailing();
        } else {
            return setAction(null);
        }
    };

    return (
        <div className={styles.liningOver}>
            <div className={styles.lining} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h1>
                        {action!==null && <IconContainer Icon={ArrowLeftIcon} onClick={funcBackButton} size = '20' color = 'black' className = '' />}
                        {action==null ? 'Выбор арбитражного управляющего'
                        :
                        (action=='withMailing' && !isApplicationDistributionWas) ? 
                        'Выбор адресов электронной почты для рассылки опроса'
                        : action=='withoutMailing' ?
                        'Выбор арбитражного управляющего вне рассылки опроса'
                        :
                        'Выбор адресов электронной почты для дополнительной рассылки опроса'}
                    </h1>
                    <IconContainer Icon={XIcon} onClick={closeModal} size = '20' color = 'black' className = '' />
                </div>
                <div className={styles.main} ref={mainRef}>
                    {caseNumber==null || caseNumber=='' || caseNumber=='--'  && <WarningPanel text='Укажите номер дела, чтобы при рассылке арбитражный управляющий мог ознакомиться с подробностями' />}
                    {action==null &&
                    <div className='selectorsWrapper'>
                        <div className='selector' onClick={() => {setAction('withMailing'); setStatus('Обратите внимание, после нажатия кнопки подтверждения рассылки отменить её будет невозможно');}}>
                            <label>Выбрать регионы для рассылки опроса</label>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="black" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                        </div>
                        <div className='selector' onClick={() => {setAction('withoutMailing'); setStatus('');}}>
                            <label>Выбрать управляющего без рассылки</label>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="black" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                        </div>
                    </div>}

                    {action!==null &&
                    <>
                        <div className='listWrapper'>
                            {action=='withMailing' &&
                            <>
                                {isApplicationDistributionWas &&
                                <>
                                    <h3>Адреса, на которые заявка уже была отправлена</h3>
                                    <div className='panel'>
                                        <div className='horizontalList'>
                                        {receivedLettersEmails.map((entry, index) => (
                                            <div
                                                key={index}
                                                className={styles.amItem}
                                                style={{width: 'fit-content'}}
                                            >
                                                <span className='userDetails'>
                                                    <p>{formatNull(entry.email)}</p>
                                                    <p className='description'>{formatDateTime(entry.created_at)}</p>
                                                </span>
                                            </div>
                                        ))}
                                        </div>
                                    </div>
                                </>}
                                <h3>Выбранные адреса для {isApplicationDistributionWas && 'дополнительной'} рассылки</h3>
                                {isAllEmailsSelected && 
                                <div style={{marginBottom: '5px'}}>
                                    <WarningPanel 
                                        text='Обратите внимание: вы выбрали все адреса арбитражных управляющих из всех регионов. Убедитесь в правильности вашего выбора, так как отправку рассылки отменить будет невозможно' 
                                        type='error'
                                    />
                                </div>}
                                <div className='panel'>
                                    <div className='horizontalList'>
                                    {
                                    selectedEmails.length===0 ? <p className='description'>Здесь будут выбранные адреса</p>
                                    : 
                                    selectedEmails.map((email, index) => (
                                        <div
                                            key={index}
                                            className={styles.amItem}
                                            style={{width: 'fit-content'}}
                                            onClick={() => toggleEmailSelect(email)}
                                        >
                                            <span className='userDetails'>
                                                <p>{formatNull(email)}</p>
                                            </span>
                                            <input 
                                                type='checkbox'
                                                checked={selectedEmails.includes(email)}
                                            />
                                        </div>
                                    ))}
                                    </div>
                                </div>

                            <div style={{height: '20px'}}></div>
                            
                            </>}

                            <h3>Поиск</h3>
                            {action=='withMailing' ?
                            <SearchAM type={action} selectedEmails={selectedEmails} toggleEmailSelect={toggleEmailSelect} />
                            :
                            <SearchAM type={action} attachAMForCase={attachAMForCase} />
                            }

                            <div style={{height: '20px'}}></div>

                            <h3>Доступные адреса арбитражных управляющих</h3>
                            {action=='withMailing' &&
                            <>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => handleSearchRegion(e.target.value)}
                                placeholder="Поиск региона..."
                                style={{position: 'sticky', top: '0px', backgroundColor: '#fff', zIndex: '12'}}
                            />
                            <div className='panel'>
                                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '10px'}}>
                                    <a style={{fontSize: '14px'}}>
                                        Отметить все адреса во всех регионах
                                    </a>
                                    <button type='button' className='miniButton' style={{width: 'fit-content'}} onClick={() => {handleToggleSelectAll()}}>
                                        {isAllEmailsSelected ? 'Убрать все' : 'Отметить все'}
                                    </button>
                                </div>
                            </div>
                            </>
                            }

                            {(regions.length===0 && loading) ? <LoadingLines />
                            : warning ? <WarningPanel text={warning} />
                            : regions.map((region) => {
                                const toggleRegionSelect = (regionId) => {
                                    const regionEmailsList = emails
                                        .filter((email) => email.region_id === regionId)
                                        .map((e) => e.email)
                                        .filter((email) => !receivedLettersEmails.some((r) => r.email === email));

                                    if (regionEmailsList.length === 0) {
                                        !expandedRegions.includes(regionId) && toggleRegionExpand(regionId);
                                        return;
                                    }

                                    if (selectedRegions.includes(regionId)) {
                                        setSelectedRegions((prev) => prev.filter((id) => id !== regionId));
                                        setSelectedEmails((prev) => prev.filter((e) => !regionEmailsList.includes(e)));
                                    } else {
                                        setSelectedRegions((prev) => [...prev, regionId]);
                                        setSelectedEmails((prev) => {
                                            const newEmails = regionEmailsList.filter((e) => !prev.includes(e));
                                            return [...prev, ...newEmails];
                                        });
                                    }
                                };

                                const isHighlighted = searchTerm.trim() && region.name.toLowerCase().includes(searchTerm.toLowerCase());



                                return(
                                <div key={region.id} className={styles.dropdownList}>
                                    <div
                                        className={styles.headForDdList}
                                        onClick={() => toggleRegionExpand(region.id)}
                                    >
                                        <a 
                                            ref={(el) => (regionRefs.current[region.id] = el)}
                                            className={`${isHighlighted ? 'highlightedText' : ''}`}
                                        >
                                            {region.name}
                                        </a>
                                        <div className='actions'>
                                            {action=='withMailing' &&
                                                <div 
                                                    className='legCheckboxWrapper' 
                                                    style={{backgroundColor: '#ffffff', padding: '5px', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #ced4da'}}
                                                    onClick={(e) => {e.stopPropagation(); toggleRegionSelect(region.id)}}
                                                >
                                                    <input 
                                                        type='checkbox' 
                                                        checked={selectedRegions.includes(region.id)}
                                                        readOnly
                                                    />
                                                    <a>Выбрать все из региона</a>
                                                </div>
                                            }
                                            {expandedRegions.includes(region.id) ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" id="Isolation_Mode" data-name="Isolation Mode" viewBox="0 0 24 24"><path d="M0,16.019,9.52,6.513a3.507,3.507,0,0,1,4.948,0L24,16.031l-2.121,2.117L12.348,8.63a.5.5,0,0,0-.707,0l-9.52,9.507Z"/></svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" data-name="Isolation Mode" viewBox="0 0 24 24"><path d="M0,8.057l9.52,9.507a3.507,3.507,0,0,0,4.948,0L24,8.046,21.879,5.929l-9.531,9.517a.5.5,0,0,1-.707,0L2.121,5.94Z"/></svg>
                                            )}
                                        </div>
                                    </div>

                                    {expandedRegions.includes(region.id) && (
                                    <div className={styles.expandedPartOfTheList}>
                                        {(emails.length===0 && loading) ? <Loading />
                                        : emails.filter((email) => email.region_id === region.id).length === 0 ? (
                                        <WarningPanel text='В данном регионе нет адресов' />
                                        ) : (
                                        emails
                                            .filter((email) => email.region_id === region.id)
                                            .map((am, index) => (
                                                <div 
                                                    className={`${styles.wideMailingLine} ${(action=='withMailing' && receivedLettersEmails.some(item => item.email === am.email)) && styles.unactive}`} 
                                                    key={index} 
                                                    onClick={() => {action=='withMailing' && toggleEmailSelect(am.email)}}
                                                >
                                                    <span className={styles.amInfoAction}>
                                                        <span className={styles.amInfo}>
                                                            {am.lastname && <p>{am.lastname} {am.firstname} {am.surname}</p>}
                                                            {am.approval=='Отключен' && <p className='description'>Аккаунт управляющего деактивирован</p>}
                                                            {am.approval=='На рассмотрении' && <p className='description'>Аккаунт управляющего на проверке после регистрации</p>}
                                                            {!am.lastname && <p className='description'>Не зарегистрирован в ЛК</p>}
                                                            <a href={`mailto:${am.email}`} className={styles.aWithLink}>{am.email}</a>
                                                            {am.lastname &&
                                                            <>
                                                                <span
                                                                    className='horizontalContainer'
                                                                >
                                                                    {am.balance && <a className={`${am.balance<0 && 'colorCell red'}`}>{am.balance + ' ₽'}</a>}
                                                                    <p className='description'>Членские взносы</p>
                                                                </span>
                                                                <span className='horizontalLine'></span>
                                                                <span
                                                                    className='horizontalContainer'
                                                                >
                                                                    {am.special_balance && <a className={`${am.special_balance<0 && 'colorCell red'}`}>{am.special_balance + ' ₽'}</a>}
                                                                    <p className='description'>Целевые взносы</p>
                                                                </span>
                                                            </>}
                                                        </span>
                                                        <span className={styles.amBtn}>
                                                            {action=='withMailing' ?
                                                            <input 
                                                                type='checkbox'
                                                                checked={selectedEmails.includes(am.email)}
                                                            />
                                                            :
                                                            <>
                                                            {am.lastname && 
                                                            (loading ? <Loading type='inline' />
                                                            : <button type='button' className='tableButton blue' onClick={() => {handleSave(am.user_id)}}>Выбрать</button>)}
                                                            </>}
                                                        </span>
                                                    </span>
                                                    {receivedLettersEmails.find(item => item.email === am.email) &&
                                                    <p className={`${styles.applicationStatus}`}>
                                                        Заявка направлялась {formatDateTime(receivedLettersEmails.find(item => item.email === am.email).created_at)}
                                                    </p>}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    )}
                                </div>
                                )
                            })}
                        </div>
                    </>}
                </div>
                <div className={styles.bottom}>
                    {action == null ? <div>Выберите вариант</div>
                    :
                    <>
                        <div>{status}</div>
                        {action=='withMailing' &&
                        <button 
                            type='button' 
                            className='button' 
                            onClick={handleSave}
                            disabled={loading}
                        >
                            Отправить {isApplicationDistributionWas && 'дополнительные'} письма
                        </button>}
                    </>}
                </div>
            </div>
        </div>
    );
};

export default AttachAM;